<section class="finance-page">
  <header class="finance-hero">
    <div>
      <p>Money control centre</p>
      <h1>Finance & budgets</h1>
      <span>Track revenue, purchases, daily expenses and budget health from one workspace.</span>
    </div>
    <div class="period">
      <button (click)="setPeriod(-1)">‹</button><strong>{{ monthName() }}</strong
      ><button (click)="setPeriod(1)">›</button>
    </div>
  </header>
  @if (error) {
    <p class="notice error">{{ error }}</p>
  }
  @if (success) {
    <p class="notice success">{{ success }}</p>
  }
  <nav class="tabs">
    <button [class.active]="activeTab === 'overview'" (click)="activeTab = 'overview'">
      Overview</button
    ><button [class.active]="activeTab === 'debts'" (click)="activeTab = 'debts'">
      Franchise partners & payables
    </button>
    @if (canManage) {
      <button [class.active]="activeTab === 'expenses'" (click)="activeTab = 'expenses'">
        Daily expenses</button
      ><button [class.active]="activeTab === 'budgets'" (click)="activeTab = 'budgets'">
        Budgets</button
      ><button [class.active]="activeTab === 'types'" (click)="activeTab = 'types'">
        Expense types
      </button>
    }
  </nav>
  @if (loading) {
    <div class="skeleton-grid">
      @for (i of [1, 2, 3, 4]; track i) {
        <span></span>
      }
    </div>
  }
  @if (!loading && summary) {
    @if (activeTab === 'overview') {
      <div class="metric-grid">
        <article class="revenue">
          <small>Revenue this month</small><strong>{{ money(summary.monthlyRevenue) }}</strong
          ><span>{{ money(summary.yearlyRevenue) }} this year</span>
        </article>
        <article class="purchase">
          <small>Stock purchases</small><strong>{{ money(summary.monthlyPurchases) }}</strong
          ><span>{{ money(summary.yearlyPurchases) }} this year</span>
        </article>
        <article class="expense">
          <small>Operating expenses</small><strong>{{ money(summary.monthlyExpenses) }}</strong
          ><span>{{ money(summary.yearlyExpenses) }} this year</span>
        </article>
        <article [class.negative]="summary.monthlyNet < 0" class="net">
          <small>Net balance</small><strong>{{ money(summary.monthlyNet) }}</strong
          ><span>{{ money(summary.yearlyNet) }} this year</span>
        </article>
      </div>
      <div class="overview-grid">
        <section class="card">
          <div class="card-title">
            <div>
              <p>Monthly budget</p>
              <h2>{{ money(summary.budgetRemaining) }} remaining</h2>
            </div>
            <strong>{{ summary.daysInMonth - summary.daysElapsed }} days left</strong>
          </div>
          <div class="progress">
            <i [style.width.%]="percent(summary.monthlyExpenses, summary.totalBudget)"></i>
          </div>
          <div class="split">
            <span>Spent {{ money(summary.monthlyExpenses) }}</span
            ><span>Budget {{ money(summary.totalBudget) }}</span>
          </div>
        </section>
        <section class="card balance">
          <p>Balance sheet snapshot</p>
          <dl>
            <div>
              <dt>Revenue</dt>
              <dd>{{ money(summary.monthlyRevenue) }}</dd>
            </div>
            <div>
              <dt>Less purchases</dt>
              <dd>- {{ money(summary.monthlyPurchases) }}</dd>
            </div>
            <div>
              <dt>Less expenses</dt>
              <dd>- {{ money(summary.monthlyExpenses) }}</dd>
            </div>
            <div class="total">
              <dt>Net result</dt>
              <dd>{{ money(summary.monthlyNet) }}</dd>
            </div>
          </dl>
        </section>
      </div>
      <div class="debt-snapshot">
        <article>
          <span class="material-symbols-outlined">call_received</span>
          <div>
            <small>Money to receive</small
            ><strong>{{ money(summary.outstandingReceivable) }}</strong>
          </div>
        </article>
        <article class="payable">
          <span class="material-symbols-outlined">call_made</span>
          <div>
            <small>Money to pay</small><strong>{{ money(summary.outstandingPayable) }}</strong>
          </div>
        </article>
      </div>
      <section class="card">
        <div class="card-title">
          <div>
            <p>Category spending</p>
            <h2>Budget performance</h2>
          </div>
        </div>
        <div class="budget-list">
          @for (item of summary.categories; track item.expenseTypeId) {
            <article>
              <div>
                <strong>{{ item.name }}</strong
                ><span>{{ money(item.spent) }} of {{ money(item.budget) }}</span>
              </div>
              <div class="progress">
                <i
                  [class.over]="item.remaining < 0"
                  [style.width.%]="percent(item.spent, item.budget)"
                ></i>
              </div>
              <small [class.danger]="item.remaining < 0">{{
                item.remaining < 0
                  ? money(-item.remaining) + ' over budget'
                  : money(item.remaining) + ' remaining'
              }}</small>
            </article>
          } @empty {
            <p>No expense types configured.</p>
          }
        </div>
      </section>
    }
    @if (activeTab === 'debts' && debts) {
      <div class="debt-metrics">
        <article class="payable">
          <small>Payable</small><strong>{{ money(debts.summary.payable) }}</strong
          ><span>You owe suppliers or parties</span>
        </article>
        <article>
          <small>Net position</small><strong>{{ money(debts.summary.net) }}</strong
          ><span>{{ debts.summary.openCount }} open records</span>
        </article>
      </div>
      @if (canManageDebts) {
        <section id="finance-entry-form" class="card form-card debt-form">
          <div class="card-title">
            <div>
              <p>Franchise funding</p>
              <h2>Add franchise agreement</h2>
              <span>Payables are created automatically from their source transactions.</span>
            </div>
          </div>
          <form (ngSubmit)="addInvestment()">
            <select name="franchiseStore" [(ngModel)]="investment.storeId" required>
              <option value="">Select franchise location</option>
              @for (store of stores; track store.id) {
                <option [value]="store.id">{{ store.name }}</option>
              }</select
            ><input
              name="investorName"
              [(ngModel)]="investment.investorName"
              placeholder="Franchise partner name *"
              required
            /><input
              name="investorPhone"
              type="tel"
              inputmode="numeric"
              autocomplete="tel"
              maxlength="10"
              pattern="[0-9]{10}"
              [ngModel]="investment.phone"
              (ngModelChange)="phoneInput('investment', $event)"
              placeholder="Phone"
            /><input
              name="investorEmail"
              type="email"
              [(ngModel)]="investment.email"
              placeholder="Portal login email *"
              autocomplete="email"
              required
            /><input
              name="principal"
              type="number"
              min="0.01"
              [(ngModel)]="investment.principalAmount"
              placeholder="Franchise fund *"
            /><input
              name="agreementNumber"
              [(ngModel)]="investment.agreementNumber"
              placeholder="Agreement number"
            /><select name="receivedPaymentMethod" [(ngModel)]="investment.paymentMethod" required><option value="">Fund received through *</option><option value="BankTransfer">Bank transfer</option><option value="UPI">UPI</option><option value="Cheque">Cheque</option><option value="Cash">Cash</option><option value="Other">Other</option></select><input name="receivedPaymentReference" [(ngModel)]="investment.paymentReference" placeholder="Payment / transaction ID (optional for cash)" /><input name="receivedPaymentNotes" [(ngModel)]="investment.paymentNotes" placeholder="Fund receipt notes (optional)" /><label class="field-label profit-share-field"
              ><span>Realized net-profit share (%) *</span
              ><input
                name="profitValue"
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                [(ngModel)]="investment.profitValue"
                placeholder="Example: 30"
                required /></label
            ><small class="profit-rule-hint"
              >Applied only to realized net profit from products funded by this franchise. Partial
              funding ratio is calculated automatically.</small
            ><input name="purpose" [(ngModel)]="investment.purpose" placeholder="Fund purpose" />
            <div class="date-row">
              <label class="field-label"
                ><span>Fund received on *</span
                ><input
                  name="investmentDate"
                  type="date"
                  [(ngModel)]="investment.investmentDate"
                  required /></label
              ><label class="field-label"
                ><span>Agreement end date (optional)</span
                ><input
                  name="maturityDate"
                  type="date"
                  [(ngModel)]="investment.maturityDate"
                  [min]="investment.investmentDate"
              /></label>
            </div>
            <label class="upload agreement-upload"
              ><span class="material-symbols-outlined">upload_file</span
              ><span>Upload signed agreement</span
              ><input
                #agreementInput
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                (change)="selectAgreement($event)"
            /></label>
            @if (agreementPreviewUrl) {
              <div class="document-preview agreement-preview">
                @if (agreementIsPdf) {
                  <a [href]="agreementPreviewUrl" target="_blank" rel="noopener"
                    ><span class="material-symbols-outlined">picture_as_pdf</span>View PDF</a
                  >
                } @else {
                  <a [href]="agreementPreviewUrl" target="_blank" rel="noopener"
                    ><img [src]="agreementPreviewUrl" alt="Agreement preview"
                  /></a>
                }
                <button
                  class="image-remove"
                  type="button"
                  title="Remove agreement"
                  aria-label="Remove agreement"
                  (click)="removeAgreement(agreementInput)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            }
            <button [disabled]="saving">
              {{ saving ? 'Uploading…' : 'Save franchise partner' }}
            </button>
          </form>
        </section>
      }
      <section class="card profit-payment-card">
        <div>
          <p>Monthly settlement</p>
          <h2>Monthly franchise withdrawal</h2>
          <span>Sold capital returns to the fund automatically. Withdraw the previous month’s earned profit, or retain it for new purchases.</span>
        </div>
        <div class="profit-payment-actions">
          <select [(ngModel)]="selectedProfitFranchiseId">
            <option value="">Select franchise partner</option>
            @for (item of debts.items; track item.id) {
              @if (item.isInvestment) {
                <option [value]="item.id">
                  {{ item.partyName }} · {{ money(item.profitOutstanding) }} total due
                </option>
              }
            }</select
          ><button
            type="button"
            [disabled]="saving || !selectedProfitFranchiseId"
            (click)="payPreviousMonthProfit()"
          >
            {{ saving ? 'Calculating…' : 'Withdraw last month profit' }}
          </button>
        </div>
      </section>
      <section class="card table-card">
        <div class="card-title">
          <div>
            <p>Outstanding ledger</p>
            <h2>Franchise partners and payables</h2>
          </div>
        </div>
        <div class="debt-list">
          @for (item of debts.items; track item.id) {
            <article [class.overdue]="item.isOverdue">
              <span
                class="debt-direction"
                [class.payable]="item.direction === 'Payable'"
                [class.receivable]="item.direction === 'Receivable'"
                >{{
                  item.isInvestment
                    ? 'Franchise'
                    : item.direction === 'Receivable'
                      ? 'To receive'
                      : 'To pay'
                }}</span
              >
              <div class="debt-party">
                <strong>{{ item.partyName }}</strong
                ><span
                  >{{ item.description || item.referenceNumber || 'No description' }} · Due
                  {{ item.dueDate || 'not set' }}</span
                >
                @if (item.isInvestment) {
                  <span
                    >Principal {{ money(item.originalAmount) }} · Accrued share
                    {{ money(item.agreementProfitAmount) }} · Allocated
                    {{ money(item.allocatedAmount) }}</span
                  >
                  <div class="investment-links">
                    @if (item.agreementUrl) {
                      <a [href]="item.agreementUrl" target="_blank">View agreement</a>
                    }
                    @if (
                      item.outstandingAmount <= 0 && item.profitOutstanding <= 0 && canManageDebts
                    ) {
                      <button type="button" class="renew-fund" (click)="renewInvestment(item)">
                        Add fund again
                      </button>
                    }
                  </div>
                }
                <div class="mini-progress">
                  <i [style.width.%]="percent(item.paidAmount, item.originalAmount)"></i>
                </div>
                <small
                  >{{ money(item.paidAmount) }} principal paid of
                  {{ money(item.originalAmount) }}</small
                >
              </div>
              <div class="debt-balance">
                <small>Total outstanding</small
                ><b>{{ money(item.totalOutstanding || item.outstandingAmount) }}</b
                ><span [class.danger]="item.isOverdue">{{
                  item.isOverdue ? 'Overdue' : item.status
                }}</span>
              </div>
              @if ((item.totalOutstanding || item.outstandingAmount) > 0 && canManageDebts) {
                <button class="pay-debt" (click)="openPayment(item)">
                  {{ item.direction === 'Receivable' ? 'Receive payment' : 'Pay amount' }}
                </button>
              }
            </article>
          } @empty {
            <div class="empty">No franchise partner or payable records yet.</div>
          }
        </div>
      </section>
    }
    @if (activeTab === 'expenses') {
      <section class="card form-card">
        <div class="card-title">
          <div>
            <p>Daily ledger</p>
            <h2>Add an expense</h2>
          </div>
        </div>
        <form (ngSubmit)="addExpense()">
          <select name="expenseType" [(ngModel)]="expense.expenseTypeId" required>
            <option value="">Select expense type</option>
            @for (type of types; track type.id) {
              @if (type.isActive) {
                <option [value]="type.id">{{ type.name }}</option>
              }
            }</select
          ><input name="date" type="date" [(ngModel)]="expense.expenseDate" required /><input
            name="amount"
            type="number"
            min="0.01"
            step="0.01"
            [(ngModel)]="expense.amount"
            placeholder="Amount"
            required
          /><input
            name="description"
            [(ngModel)]="expense.description"
            placeholder="What was this expense for?"
            required
          /><input
            name="reference"
            [(ngModel)]="expense.referenceNumber"
            placeholder="Bill / reference (optional)"
          /><select name="payment" [(ngModel)]="expense.paymentMethod">
            <option value="">Payment method</option>
            <option>Cash</option>
            <option>UPI</option>
            <option>Card</option>
            <option>Bank transfer</option></select
          ><button [disabled]="saving">{{ saving ? 'Saving…' : 'Save expense' }}</button>
        </form>
      </section>
      <section class="card table-card">
        <div class="card-title">
          <div>
            <p>{{ monthName() }}</p>
            <h2>Expense ledger</h2>
          </div>
          <strong>{{ money(summary.monthlyExpenses) }}</strong>
        </div>
        <div class="expense-list">
          @for (item of expenses; track item.id) {
            <article>
              <div class="expense-icon">receipt_long</div>
              <div>
                <strong>{{ item.description }}</strong
                ><span
                  >{{ item.expenseTypeName }} · {{ item.expenseDate }} ·
                  {{ item.paymentMethod || 'Payment not set' }}</span
                >
              </div>
              <b>{{ money(item.amount) }}</b
              ><button class="delete" (click)="removeExpense(item)">delete</button>
            </article>
          } @empty {
            <div class="empty">No expenses recorded for this month.</div>
          }
        </div>
      </section>
    }
    @if (activeTab === 'budgets') {
      <section class="card form-card">
        <div class="card-title">
          <div><p>Controlled purchasing balance</p><h2>Company inventory fund</h2></div>
          <span>{{ money(companyFund?.totalBalance) }} available across all locations</span>
        </div>
        <form (ngSubmit)="saveCompanyFund()">
          <select name="companyFundStore" [(ngModel)]="companyFundEntry.storeId" required>
            <option value="">Select inventory location</option>
            @for (store of stores; track store.id) { <option [value]="store.id">{{ store.name }}</option> }
          </select>
          <select name="companyFundType" [(ngModel)]="companyFundEntry.type"><option value="Deposit">Add company fund</option><option value="Withdrawal">Withdraw / use as expense</option></select>
          <input name="companyFundAmount" type="number" min="0.01" step="0.01" [(ngModel)]="companyFundEntry.amount" placeholder="Amount" required />
          <input name="companyFundReference" [(ngModel)]="companyFundEntry.referenceNumber" placeholder="Bank / account reference (optional)" />
          <input name="companyFundNotes" [(ngModel)]="companyFundEntry.notes" placeholder="Purpose or notes" />
          <button [disabled]="saving">{{ saving ? 'Saving…' : 'Save fund entry' }}</button>
        </form>
        <div class="budget-list">
          @for (account of companyFund?.accounts ?? []; track account.id) {
            <article><div><strong>{{ storeName(account.storeId) }}</strong><span>Available for inventory purchases</span></div><b>{{ money(account.balance) }}</b></article>
          } @empty { <div class="empty">No company inventory fund has been added yet.</div> }
        </div>
      </section>
      <section class="card">
        <div class="card-title">
          <div>
            <p>{{ monthName() }}</p>
            <h2>Monthly budgets</h2>
          </div>
          <span>Set advertising and every other operating budget independently.</span>
        </div>
        <div class="budget-editor">
          @for (item of summary.categories; track item.expenseTypeId) {
            <article>
              <div>
                <strong>{{ item.name }}</strong
                ><span>Spent {{ money(item.spent) }}</span>
              </div>
              <input
                type="number"
                min="0"
                [(ngModel)]="budgetDrafts[item.expenseTypeId]"
                placeholder="Monthly budget"
              /><button [disabled]="saving" (click)="saveBudget(item.expenseTypeId)">Save</button>
            </article>
          }
        </div>
      </section>
    }
    @if (activeTab === 'types') {
      <section class="card form-card">
        <div class="card-title">
          <div>
            <p>Master data</p>
            <h2>Expense types</h2>
          </div>
        </div>
        <form (ngSubmit)="addType()">
          <input
            name="typeName"
            [(ngModel)]="newType.name"
            placeholder="Expense type name"
            required
          /><input
            name="typeDescription"
            [(ngModel)]="newType.description"
            placeholder="Description (optional)"
          /><button [disabled]="saving">Add type</button>
        </form>
        <div class="type-list">
          @for (type of types; track type.id) {
            <article>
              <div>
                <strong>{{ type.name }}</strong
                ><span>{{ type.description || 'No description' }}</span>
              </div>
              <button [class.off]="!type.isActive" (click)="toggleType(type)">
                {{ type.isActive ? 'Active' : 'Inactive' }}
              </button>
            </article>
          }
        </div>
      </section>
    }
  }
  @if (payingDebt) {
    <div class="modal-backdrop" (click)="closePayment()">
      <section class="payment-modal" (click)="$event.stopPropagation()">
        <div class="card-title">
          <div>
            <p>{{ payingDebt.isInvestment ? 'Franchise agreement' : payingDebt.direction }}</p>
            <h2>{{ payingDebt.direction === 'Receivable' ? 'Receive payment' : 'Pay amount' }}</h2>
            <span
              >{{ payingDebt.partyName }} ·
              {{
                money(payingDebt.totalOutstanding || payingDebt.outstandingAmount)
              }}
              outstanding</span
            >
          </div>
          <button class="modal-close" (click)="closePayment()">close</button>
        </div>
        <form (ngSubmit)="payDebt()">
          @if (lastMonthProfit) {
            <div class="monthly-profit-summary">
              <span>{{ lastMonthProfit.monthLabel }}</span
              ><strong>{{ money(lastMonthProfit.amountToPay) }} to pay</strong
              ><small
                >Realized profit {{ money(lastMonthProfit.realisedProfit) }} · Accrued share
                {{ money(lastMonthProfit.accruedProfit) }}</small
              >
            </div>
          }
          @if (payingDebt.isInvestment) {
            <select
              name="paymentComponent"
              [(ngModel)]="debtPayment.component"
              (ngModelChange)="
                debtPayment.amount =
                  $event === 'Profit' ? payingDebt.profitOutstanding : payingDebt.outstandingAmount
              "
            >
              <option value="Principal">
                Pay principal ({{ money(payingDebt.outstandingAmount) }} due)
              </option>
              <option value="Profit">
                Pay agreed profit ({{ money(payingDebt.profitOutstanding) }} due)
              </option>
            </select>
          }
          <input
            name="payAmount"
            type="number"
            min="0.01"
            [max]="
              debtPayment.component === 'Profit'
                ? payingDebt.profitOutstanding
                : payingDebt.outstandingAmount
            "
            step="0.01"
            [(ngModel)]="debtPayment.amount"
            placeholder="Payment amount"
            required
          /><input
            name="payDate"
            type="date"
            [(ngModel)]="debtPayment.paymentDate"
            required
          /><select name="payMethod" [(ngModel)]="debtPayment.paymentMethod">
            <option>Cash</option>
            <option>UPI</option>
            <option>Card</option>
            <option>Bank transfer</option>
            <option>Cheque</option></select
          ><input
            name="payReference"
            [(ngModel)]="debtPayment.referenceNumber"
            placeholder="Transaction / reference"
          /><textarea
            name="payNotes"
            [(ngModel)]="debtPayment.notes"
            placeholder="Payment notes"
          ></textarea>
          @if (payingDebt.direction === 'Payable') {
            <label class="expense-check"
              ><input
                type="checkbox"
                name="recordExpense"
                [(ngModel)]="debtPayment.recordAsExpense"
              />
              Also record this payment as an expense</label
            >
            @if (debtPayment.recordAsExpense) {
              <select name="debtExpenseType" [(ngModel)]="debtPayment.expenseTypeId" required>
                <option value="">Select expense type</option>
                @for (type of types; track type.id) {
                  @if (type.isActive) {
                    <option [value]="type.id">{{ type.name }}</option>
                  }
                }
              </select>
            }
          }
          <button class="primary-action" type="submit" [disabled]="saving">
            {{ saving ? 'Recording…' : 'Confirm payment' }}
          </button>
        </form>
      </section>
    </div>
  }
  @if (allocatingDebt) {
    <div class="modal-backdrop" (click)="allocatingDebt = null">
      <section class="payment-modal" (click)="$event.stopPropagation()">
        <div class="card-title">
          <div>
            <p>Fund utilisation</p>
            <h2>Allocate franchise fund to product</h2>
            <span
              >{{
                money(allocatingDebt.originalAmount - allocatingDebt.allocatedAmount)
              }}
              unallocated</span
            >
          </div>
          <button class="modal-close" (click)="allocatingDebt = null">close</button>
        </div>
        <form (ngSubmit)="allocateInvestment()">
          <select name="allocationProduct" [(ngModel)]="allocation.purchaseItemId" required>
            <option value="">Select purchased product</option>
            @for (product of investmentProducts; track product.id) {
              <option [value]="product.id">
                {{ product.title }} · {{ product.voucherNumber }} · {{ money(product.lineTotal) }}
              </option>
            }</select
          ><input
            name="allocationAmount"
            type="number"
            min="0.01"
            [max]="allocatingDebt.originalAmount - allocatingDebt.allocatedAmount"
            [(ngModel)]="allocation.amount"
            placeholder="Amount used"
          /><textarea
            name="allocationNotes"
            [(ngModel)]="allocation.notes"
            placeholder="How the fund was used"
          ></textarea
          ><button [disabled]="saving">Allocate amount</button>
        </form>
      </section>
    </div>
  }
</section>
